import type { AssessmentState } from "./storage";
import type { CareerMatchResult } from "../scoring";

export const PROFILE_BANDS = ["strong", "good", "developing", "priority"] as const;
export type ProfileBand = (typeof PROFILE_BANDS)[number];

export interface ProfileCategoryScore {
  groupId: string;
  percent: number;
}

export interface ProfilePriority {
  skillId: string;
  priority: number;
}

export interface ProfilePayload {
  version: 1;
  careerId: string;
  field?: string;
  stage?: string;
  clusterId?: string;
  matchPercent: number;
  band: ProfileBand;
  categoryScores: ProfileCategoryScore[];
  strengths: string[];
  priorities: ProfilePriority[];
  priorityGroupIds: string[];
  learningResourceIds: string[];
  completedAt: string;
}

const ID = /^[a-z0-9-]{2,64}$/;

function cleanId(value: string | undefined): string | undefined {
  if (!value || !ID.test(value)) return undefined;
  return value;
}

export function buildProfilePayload(
  state: AssessmentState,
  result: CareerMatchResult,
  learningResourceIds: string[],
): ProfilePayload | null {
  const careerId = cleanId(state.careerId);
  if (!careerId) return null;
  const band = PROFILE_BANDS.includes(result.band) ? result.band : "developing";
  return {
    version: 1,
    careerId,
    field: state.field,
    stage: cleanId(state.stage),
    clusterId: cleanId(state.clusterId),
    matchPercent: Math.round(result.percent * 100) / 100,
    band,
    categoryScores: result.categoryScores.map((c) => ({
      groupId: c.groupId,
      percent: Math.round(c.percent * 100) / 100,
    })),
    strengths: result.strengths.map((s) => s.skillId).filter((id) => ID.test(id)),
    priorities: result.priorities.map((s) => ({
      skillId: s.skillId,
      priority: s.priority,
    })),
    priorityGroupIds: state.priorityGroupIds.filter((id) => ID.test(id)).slice(0, 3),
    learningResourceIds: learningResourceIds.filter((id) => ID.test(id)).slice(0, 8),
    completedAt: state.completedAt ?? new Date().toISOString(),
  };
}

export function printSkillsProfile(): void {
  const root = document.documentElement;
  const done = () => root.classList.remove("obs-printing");
  root.classList.add("obs-printing");
  window.addEventListener("afterprint", done, { once: true });
  window.setTimeout(() => window.print(), 50);
}
